import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  LayoutGrid, RefreshCw, Search, TrendingUp, TrendingDown, 
  Target, Clock, Percent, Activity, Plus, Check, Maximize2, Minimize2, Fullscreen
} from 'lucide-react';
import { toast } from 'sonner';
import OptionChainModal from './OptionChainModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OptionChainPanel = ({ onSelectOption, isExpanded, onToggleExpand }) => {
  const [symbol, setSymbol] = useState('');
  const [loading, setLoading] = useState(false);
  const [chainData, setChainData] = useState(null);
  const [selectedExpiration, setSelectedExpiration] = useState('');
  const [selectedStrikes, setSelectedStrikes] = useState([]);
  const [activeTab, setActiveTab] = useState('calls');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [strikesFilter, setStrikesFilter] = useState('20'); // Number of strikes to show

  const fetchChain = useCallback(async (exp = null) => {
    if (!symbol.trim()) {
      toast.error('Please enter a stock symbol');
      return;
    }
    
    setLoading(true);
    
    try {
      const expParam = exp || selectedExpiration;
      const url = expParam 
        ? `${API}/data/chain-greeks/${symbol.toUpperCase()}?expiration=${expParam}`
        : `${API}/data/chain-greeks/${symbol.toUpperCase()}`;
      
      const response = await axios.get(url);
      setChainData(response.data);
      
      if (!selectedExpiration && response.data.expiration) {
        setSelectedExpiration(response.data.expiration);
      }
      
      toast.success(`Loaded ${response.data.calls_count} calls, ${response.data.puts_count} puts`);
    } catch (err) {
      console.error('Error fetching chain:', err);
      toast.error(err.response?.data?.detail || 'Failed to fetch option chain');
    } finally {
      setLoading(false);
    }
  }, [symbol, selectedExpiration]);

  const handleExpirationChange = (exp) => {
    setSelectedExpiration(exp);
    fetchChain(exp);
  };

  const toggleStrikeSelection = (strike, optionType, optionData) => {
    const key = `${strike}-${optionType}`;
    const isSelected = selectedStrikes.some(s => s.key === key);
    
    if (isSelected) {
      setSelectedStrikes(prev => prev.filter(s => s.key !== key));
    } else {
      const newSelection = {
        key,
        strike,
        optionType,
        symbol: chainData.symbol,
        expiration: chainData.expiration,
        ...optionData
      };
      setSelectedStrikes(prev => [...prev, newSelection]);
      
      // Callback to parent if provided
      if (onSelectOption) {
        onSelectOption(newSelection);
      }
    }
  };

  const isStrikeSelected = (strike, optionType) => {
    return selectedStrikes.some(s => s.key === `${strike}-${optionType}`);
  };

  const clearSelections = () => {
    setSelectedStrikes([]);
  };

  // Filter strikes around ATM
  const filterStrikes = (options) => {
    if (!options || options.length === 0) return [];
    if (strikesFilter === 'all') return options;
    
    const count = parseInt(strikesFilter);
    
    // Find index of ATM or closest to current price
    let atmIndex = -1;
    const targetPrice = chainData?.current_price || chainData?.atm_strike;
    
    if (targetPrice) {
      let minDiff = Infinity;
      options.forEach((o, i) => {
        const diff = Math.abs(o.strike - targetPrice);
        if (diff < minDiff) {
          minDiff = diff;
          atmIndex = i;
        }
      });
    }
    
    if (atmIndex === -1) return options.slice(0, count);
    
    const halfCount = Math.floor(count / 2);
    const start = Math.max(0, atmIndex - halfCount);
    const end = Math.min(options.length, start + count);
    return options.slice(start, end);
  };

  const formatDelta = (delta) => {
    if (delta === null || delta === undefined) return '-';
    return delta.toFixed(2);
  };

  const formatChange = (change) => {
    if (!change) return <span className="text-gray-400">0.00</span>;
    const color = change > 0 ? 'text-green-600' : change < 0 ? 'text-red-600' : 'text-gray-400';
    const prefix = change > 0 ? '+' : '';
    return <span className={color}>{prefix}{change.toFixed(2)}</span>;
  };

  const OptionRow = ({ option, optionType, isATM }) => {
    const selected = isStrikeSelected(option.strike, optionType);
    
    return (
      <div 
        className={`
          grid grid-cols-10 gap-1 py-1.5 px-2 text-xs border-b border-gray-100 cursor-pointer
          transition-all duration-150 hover:bg-blue-50
          ${isATM ? 'bg-yellow-50 border-yellow-200' : ''}
          ${selected ? 'bg-blue-100 border-blue-300' : ''}
          ${option.itm ? 'bg-gray-50' : ''}
        `}
        onClick={() => toggleStrikeSelection(option.strike, optionType, option)}
        data-testid={`option-row-${option.strike}-${optionType}`}
      >
        {/* Selection indicator */}
        <div className="flex items-center justify-center">
          {selected ? (
            <Check className="h-3 w-3 text-blue-600" />
          ) : (
            <Plus className="h-3 w-3 text-gray-300 hover:text-blue-500" />
          )}
        </div>
        
        {/* Strike */}
        <div className={`font-medium ${isATM ? 'text-yellow-700' : ''}`}>
          {option.strike.toFixed(1)}
          {isATM && <span className="ml-1 text-[9px] text-yellow-600">ATM</span>}
        </div>
        
        {/* Bid */}
        <div className="text-right">{option.bid?.toFixed(2) || '-'}</div>
        
        {/* Ask */}
        <div className="text-right">{option.ask?.toFixed(2) || '-'}</div>
        
        {/* Last */}
        <div className="text-right font-medium">{option.last?.toFixed(2) || '-'}</div>
        
        {/* Change */}
        <div className="text-right">{formatChange(option.change)}</div>
        
        {/* Delta */}
        <div className="text-right text-blue-600">{formatDelta(option.delta)}</div>
        
        {/* Theta */}
        <div className="text-right text-cyan-600">{option.theta?.toFixed(3) || '-'}</div>
        
        {/* IV */}
        <div className="text-right text-purple-600">{option.iv}%</div>
        
        {/* OTM% */}
        <div className="text-right">
          {option.otm_pct > 0 ? (
            <span className="text-orange-600">{option.otm_pct.toFixed(1)}%</span>
          ) : (
            <span className="text-gray-400">ITM</span>
          )}
        </div>
      </div>
    );
  };

  const OptionTable = ({ options, optionType }) => {
    const filteredOptions = filterStrikes(options || []);
    
    // Find closest strike to current price for ATM highlighting
    const getIsATM = (strike) => {
      if (!chainData?.current_price || !filteredOptions.length) return false;
      let minDiff = Infinity;
      let atmStrike = null;
      filteredOptions.forEach(o => {
        const diff = Math.abs(o.strike - chainData.current_price);
        if (diff < minDiff) {
          minDiff = diff;
          atmStrike = o.strike;
        }
      });
      return strike === atmStrike;
    };
    
    return (
      <div className="text-xs">
        {/* Header */}
        <div className="grid grid-cols-10 gap-1 py-2 px-2 bg-gray-100 font-semibold text-gray-600 sticky top-0">
          <div></div>
          <div>Strike</div>
          <div className="text-right">Bid</div>
          <div className="text-right">Ask</div>
          <div className="text-right">Last</div>
          <div className="text-right">Chg</div>
          <div className="text-right text-blue-600">Δ</div>
          <div className="text-right text-cyan-600">Θ</div>
          <div className="text-right text-purple-600">IV</div>
          <div className="text-right text-orange-600">OTM%</div>
        </div>
        
        {/* Rows - dynamic height based on expanded state */}
        <ScrollArea className={isExpanded ? 'h-[calc(100vh-320px)]' : 'h-[350px]'}>
          {filteredOptions.map((option) => (
            <OptionRow 
              key={option.strike}
              option={option}
              optionType={optionType}
              isATM={getIsATM(option.strike)}
            />
          ))}
          {filteredOptions.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No options data available
            </div>
          )}
        </ScrollArea>
      </div>
    );
  };

  return (
    <>
    <Card className="glass-effect border-0 shadow-lg h-full flex flex-col">
      <CardHeader className="pb-2 flex-shrink-0">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center text-lg">
            <LayoutGrid className="h-5 w-5 mr-2 text-indigo-600" />
            Option Chain
          </CardTitle>
          <div className="flex items-center space-x-1">
            {/* Full Screen Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsModalOpen(true)}
              className="h-8 px-2 hover:bg-indigo-100"
              data-testid="fullscreen-option-chain-btn"
              title="Open full screen view"
            >
              <Fullscreen className="h-4 w-4 text-indigo-600" />
            </Button>
            {onToggleExpand && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onToggleExpand}
                className="h-8 px-2 hover:bg-indigo-100"
                data-testid="expand-option-chain-btn"
                title={isExpanded ? "Collapse panel" : "Expand panel"}
              >
                {isExpanded ? (
                  <Minimize2 className="h-4 w-4 text-indigo-600" />
                ) : (
                  <Maximize2 className="h-4 w-4 text-indigo-600" />
                )}
              </Button>
            )}
          </div>
        </div>
        <CardDescription>
          Click to select strikes for strategies
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-3 flex-1 overflow-hidden flex flex-col">
        {/* Search Input */}
        <div className="flex space-x-2">
          <Input
            placeholder="Symbol (e.g., SPY)"
            value={symbol}
            onChange={(e) => setSymbol(e.target.value.toUpperCase())}
            onKeyPress={(e) => e.key === 'Enter' && fetchChain()}
            className="flex-1"
            data-testid="option-chain-symbol-input"
          />
          <Button 
            onClick={() => fetchChain()} 
            disabled={loading}
            className="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600"
            data-testid="option-chain-search-btn"
          >
            {loading ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
          </Button>
        </div>

        {/* Chain Data */}
        {chainData && (
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Stock Info Bar */}
            <div className="flex items-center justify-between text-sm mb-2 px-1">
              <div className="flex items-center space-x-3">
                <span className="font-bold text-lg">{chainData.symbol}</span>
                <span className="text-green-600 font-semibold">${chainData.current_price}</span>
              </div>
              <Badge variant="outline" className="text-xs">
                <Clock className="h-3 w-3 mr-1" />
                {chainData.days_to_exp} DTE
              </Badge>
            </div>

            {/* Controls Row */}
            <div className="flex items-center space-x-2 mb-2">
              {/* Expiration Select */}
              <Select value={selectedExpiration} onValueChange={handleExpirationChange}>
                <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="expiration-select">
                  <SelectValue placeholder="Expiration" />
                </SelectTrigger>
                <SelectContent>
                  {chainData.expirations?.map((exp) => (
                    <SelectItem key={exp} value={exp} className="text-xs">
                      {exp}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Strikes Filter */}
              <Select value={strikesFilter} onValueChange={setStrikesFilter}>
                <SelectTrigger className="w-[90px] h-8 text-xs">
                  <SelectValue placeholder="Strikes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10 Strikes</SelectItem>
                  <SelectItem value="20">20 Strikes</SelectItem>
                  <SelectItem value="30">30 Strikes</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>

              {/* Refresh */}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => fetchChain()}
                disabled={loading}
                className="h-8 px-2"
              >
                <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>

            {/* Selected Strikes */}
            {selectedStrikes.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2 p-2 bg-blue-50 rounded-lg">
                <span className="text-xs text-blue-700 font-medium mr-1">Selected:</span>
                {selectedStrikes.map((s) => (
                  <Badge 
                    key={s.key} 
                    variant="secondary" 
                    className="text-[10px] cursor-pointer hover:bg-red-100"
                    onClick={() => toggleStrikeSelection(s.strike, s.optionType, s)}
                  >
                    {s.strike} {s.optionType.charAt(0).toUpperCase()}
                    <span className="ml-1 text-gray-400">×</span>
                  </Badge>
                ))}
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={clearSelections}
                  className="h-5 text-[10px] text-red-600 hover:text-red-700 px-1"
                >
                  Clear All
                </Button>
              </div>
            )}

            {/* Calls/Puts Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
              <TabsList className="grid w-full grid-cols-2 h-8">
                <TabsTrigger value="calls" className="text-xs data-[state=active]:bg-green-100 data-[state=active]:text-green-700">
                  <TrendingUp className="h-3 w-3 mr-1" />
                  Calls ({chainData.calls_count})
                </TabsTrigger>
                <TabsTrigger value="puts" className="text-xs data-[state=active]:bg-red-100 data-[state=active]:text-red-700">
                  <TrendingDown className="h-3 w-3 mr-1" />
                  Puts ({chainData.puts_count})
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="calls" className="flex-1 overflow-hidden mt-2">
                <OptionTable options={chainData.calls} optionType="call" />
              </TabsContent>
              
              <TabsContent value="puts" className="flex-1 overflow-hidden mt-2">
                <OptionTable options={chainData.puts} optionType="put" />
              </TabsContent>
            </Tabs>

            {/* Legend */}
            <div className="flex items-center justify-center space-x-4 text-[10px] text-gray-500 pt-2 border-t">
              <span className="flex items-center">
                <div className="w-3 h-3 bg-yellow-100 border border-yellow-300 rounded mr-1"></div>
                ATM
              </span>
              <span className="flex items-center">
                <div className="w-3 h-3 bg-gray-100 rounded mr-1"></div>
                ITM
              </span>
              <span className="flex items-center">
                <div className="w-3 h-3 bg-blue-100 border border-blue-300 rounded mr-1"></div>
                Selected
              </span>
            </div>

            {/* Cached indicator */}
            {chainData.cached && (
              <p className="text-[10px] text-center text-amber-600 mt-1">
                Cached • Last updated {new Date(chainData.timestamp).toLocaleTimeString()}
              </p>
            )}
          </div>
        )}

        {/* Initial State */}
        {!chainData && !loading && (
          <div className="text-center py-8 text-gray-500 flex-1 flex flex-col items-center justify-center">
            <LayoutGrid className="h-10 w-10 mx-auto mb-3 text-gray-300" />
            <p className="text-sm font-medium">Enter a symbol to load option chain</p>
            <p className="text-xs mt-1">Includes Delta, Theta, IV, OTM%</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsModalOpen(true)}
              className="mt-3"
            >
              <Fullscreen className="h-4 w-4 mr-2" />
              Open Full Screen
            </Button>
          </div>
        )}
      </CardContent>
    </Card>

    {/* Full Screen Modal */}
    <OptionChainModal 
      isOpen={isModalOpen} 
      onClose={() => setIsModalOpen(false)}
      initialSymbol={symbol}
    />
    </>
  );
};

export default OptionChainPanel;
